import { FC, useMemo, useState } from 'react';

import {
  PublicSuggestedConnection,
  PublicUserConnection,
  PublicUserConnectionStatus,
  UserConnectionStatus,
} from '@shared/types/userConnection';

import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

import {
  useInviteConnectionMutation,
  useRespondToConnectionRequest,
  useSendConnectionRequest,
} from '@mutations/UserConnectionMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useContacts } from '@hooks/useContacts';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { List, ListItemProps } from '@components/layout/List/List';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { UserAvatar } from '@components/layout/UserAvatar/UserAvatar';

const PendingConnectionListItemEndIcon: FC<{ connection: PublicUserConnection }> = ({
  connection,
}) => {
  const { track } = useAnalytics();
  const { isRespondingToConnectionRequest, respondToConnectionRequestAsync } =
    useRespondToConnectionRequest();

  const handleAcceptConnection = async (connection: PublicUserConnection) => {
    track('Accept connection', {
      connectionId: connection._id,
    });

    await respondToConnectionRequestAsync({
      connectionId: connection._id,
      status: UserConnectionStatus.Accepted,
    });
  };

  const handleDeclineConnection = async (connection: PublicUserConnection) => {
    track('Decline connection', {
      connectionId: connection._id,
    });

    await respondToConnectionRequestAsync({
      connectionId: connection._id,
      status: UserConnectionStatus.Rejected,
    });
  };

  return (
    <div slot="after-title">
      <div className="d-flex gap-1">
        <Button
          size="sm"
          disabled={isRespondingToConnectionRequest}
          onClick={() => handleAcceptConnection(connection)}
        >
          Accept
        </Button>
        <Button
          variant="outlined"
          color="neutral"
          size="sm"
          disabled={isRespondingToConnectionRequest}
          onClick={() => handleDeclineConnection(connection)}
        >
          Decline
        </Button>
      </div>
    </div>
  );
};

const SuggestedConnectionListItemEndIcon: FC<{ connection: PublicSuggestedConnection }> = ({
  connection,
}) => {
  const { track } = useAnalytics();
  const [didConnect, setDidConnect] = useState(false);
  const { isSendingConnectionRequest, sendConnectionRequestAsync } = useSendConnectionRequest();

  const handleConnect = async (connection: PublicSuggestedConnection) => {
    track('Accept suggested connection', {
      connectionId: connection.connectionUserId,
    });

    setDidConnect(true);

    await sendConnectionRequestAsync({
      toUserId: connection.connectionUserId,
    });
  };

  return (
    <div slot="after-title">
      <div className="d-flex gap-1">
        <Button
          size="sm"
          disabled={isSendingConnectionRequest || didConnect}
          onClick={() => handleConnect(connection)}
        >
          {didConnect ? 'Send!' : 'Connect'}
        </Button>
      </div>
    </div>
  );
};

export const ConnectionsContent: FC<{
  onClickConnect: () => void;
  suggestedConnectionUserId?: string;
}> = ({ onClickConnect, suggestedConnectionUserId }) => {
  const { isContactFeatureEnabled } = useContacts();
  const {
    connectionsAreReady,
    connections = [],
    suggestedConnections = [],
  } = useUserConnectionsQuery();
  const { currentUser } = useCurrentUser();

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const handleConnectionClick = (connection: PublicUserConnection | PublicSuggestedConnection) => {
    navigate(`/noauth/ucc/${connection.connectionUserId}`);
  };

  const suggestedConnection = suggestedConnectionUserId
    ? suggestedConnections.find(
        (connection) => connection.connectionUserId === suggestedConnectionUserId,
      )
    : undefined;

  const acceptedConnections = connections.filter(
    (connection) => connection.status === PublicUserConnectionStatus.Accepted,
  );

  const pendingConnections = connections.filter(
    (connection) => connection.status === PublicUserConnectionStatus.PendingReceived,
  );

  const listItems = useMemo<ListItemProps[]>(() => {
    const mappedListItems = [
      ...(suggestedConnection
        ? [
            {
              startIcon: (
                <UserAvatar
                  displayName={suggestedConnection.connectionName}
                  avatarUrl={suggestedConnection.connectionAvatar}
                />
              ),
              title: suggestedConnection.connectionName,
              subTitle: `Suggested`,
              endIcon: <SuggestedConnectionListItemEndIcon connection={suggestedConnection} />,
            },
          ]
        : []),
      ...pendingConnections.map((connection) => ({
        startIcon: (
          <UserAvatar
            displayName={connection.connectionName}
            avatarUrl={connection.connectionAvatar}
          />
        ),
        title: connection.connectionName,
        subTitle: `Pending`,
        endIcon: <PendingConnectionListItemEndIcon connection={connection} />,
      })),
      ...acceptedConnections.map((connection) => ({
        startIcon: (
          <UserAvatar
            displayName={connection.connectionName}
            avatarUrl={connection.connectionAvatar}
          />
        ),
        title: connection.connectionName,
        subTitle: `${connection.goingEventCount ?? 0} going, ${connection.maybeEventCount ?? 0} maybe`,
        onClick: () => handleConnectionClick(connection),
      })),
    ];

    if (isContactFeatureEnabled) {
      mappedListItems.push({
        startIcon: <FontAwesomeIcon name="user-plus" />,
        title: 'Sync contacts',
        subTitle: 'Download the app to connect with your contacts',
        onClick: () => {},
      });
    }

    return mappedListItems;
  }, [acceptedConnections, isContactFeatureEnabled]);

  if (!connectionsAreReady) {
    return <Skeleton height={120} />;
  }

  if (!listItems.length) {
    return (
      <EmptyState
        title="Add your first connection"
        text="Connections see each other's plans. Start with a few close friends — you can always add more!"
        button={{
          text: 'Invite a friend',
          onClick: onClickConnect,
        }}
      />
    );
  }

  return (
    <>
      <List className="mt-0" ignoreScreenContentPadding listItems={listItems} />
    </>
  );
};
